import { Component, computed, effect, ElementRef, inject, signal, untracked, viewChild } from '@angular/core';
import { GemstoneArrangement } from 'src/app/interfaces/gemstone-arrangement';
import { GemstoneSize } from 'src/app/interfaces/gemstone-size';
import { GemstoneType } from 'src/app/interfaces/gemstone-type';
import { SelectedAttributeService } from '../../../services/selected-attribute.service';
import { StoneTypesWithShapesService } from 'src/app/services/stone-types-with-shapes/stone-types-with-shapes.service';
import { StoneSizesService } from 'src/app/services/stone-sizes/stone-sizes.service';
import { StoneArrangementsWithSettingsService } from 'src/app/services/stone-arrangementsWithSettings/stone-arrangements-with-settings.service';
import { GemstoneTypeWithShape } from 'src/app/interfaces/gemstone-type-with-shape';
import { GemstoneArrangementWithSetting } from 'src/app/interfaces/gemstone-arrangement-with-setting';
import { StoneArrangementsService } from 'src/app/services/stone-arrangements/stone-arrangements.service';
import { StoneShapesService } from 'src/app/services/stone-shapes/stone-shapes.service';
import { StoneTypesService } from 'src/app/services/stone-types/stone-types.service';
import { StoneSettingsService } from 'src/app/services/stone-settings/stone-settings.service';
import { assert } from 'src/app/utils/assert.util';
import { LoadingService } from 'src/app/services/loading/loading.service';
import { RulesService } from 'src/app/services/rules/rules.service';
import { CheckoutService } from '../../../services/checkout.service';
import { GoogleAnalyticsService } from 'src/app/services/google-analytics/google-analytics.service';
import { UnavailableDialogComponent } from '../../unavailable.dialog/unavailable.dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { Navigation2Service } from '../../../services/navigation2.service';
import { StoneCountsProviderService } from 'src/app/providers/stone-counts/stone-counts.provider.service';
import { ScrollService } from 'src/app/services/scroll/scroll.service';

@Component({
  selector: 'app-gemstones',
  templateUrl: './gemstones.component.html',
  styleUrl: './gemstones.component.scss'
})
export class GemstonesComponent {
  private scrollService = inject(ScrollService);
  private stoneCountsProvider = inject(StoneCountsProviderService);
  private navigationService = inject(Navigation2Service);
  private dialog = inject(MatDialog);
  private googleAnalyticsService = inject(GoogleAnalyticsService);
  private checkoutService = inject(CheckoutService);
  private rulesService = inject(RulesService);
  private loadingService = inject(LoadingService);
  private stoneTypesWithShapesService = inject(StoneTypesWithShapesService);
  private stoneSizesService = inject(StoneSizesService);
  private stoneArrangementsWithSettingsService = inject(StoneArrangementsWithSettingsService);
  private stoneArrangementsService = inject(StoneArrangementsService);
  private stoneShapesService = inject(StoneShapesService);
  private stoneTypesService = inject(StoneTypesService);
  private stoneSettingsService = inject(StoneSettingsService);
  private selectedAttributeService = inject(SelectedAttributeService);
  container = viewChild('container', { read: ElementRef });
  shouldScroll = signal(true);
  selectedType = computed(() => this.selectedAttributeService.configuredItem().gemstoneType);
  selectedSize = computed(() => this.selectedAttributeService.configuredItem().gemstoneSize);
  selectedArrangement = computed(() => this.selectedAttributeService.configuredItem().gemstoneArrangement);
  selectedSetting = computed(() => this.selectedAttributeService.configuredItem().gemstoneSetting);
  selectedShape = computed(() => this.selectedAttributeService.configuredItem().gemstoneShape);
  stoneCount = signal<number | null>(null);
  availableTypesWithShapes = signal<number[]>([]);
  availableArrangementsWithSettings = signal<number[]>([]);
  availableStoneSizes = signal<number[]>([]);
  reachableTypesWithShapes = signal<number[]>([]);
  reachableArrangementsWithSettings = signal<number[]>([]);
  reachableStoneSizes = signal<number[]>([]);
  types = computed(() => this.stoneTypesWithShapesService.values());
  sizes = computed(() => this.stoneSizesService.values());
  arrangements = computed(() => this.stoneArrangementsWithSettingsService.values());
  typesToShow = computed(() => {
    const selectedType = this.selectedType();
    const selectedShape = this.selectedShape();
    const isShowingUnavailable = this.selectedAttributeService.shouldShowUnavailable();
    const reachableTypes = this.reachableTypesWithShapes();
    const types = this.types()
    if (isShowingUnavailable) {
      const sortedArrangements = types.sort((a, b) => (reachableTypes.includes(b.id) ? 1 : 0) - (reachableTypes.includes(a.id) ? 1 : 0));
      return sortedArrangements;
    } else {
      return types.filter(w => reachableTypes.includes(w.id) || (w.stoneTypeID === selectedType?.id && w.stoneShapeID === selectedShape?.id));
    }
  });
  arrangementsToShow = computed(() => {
    const selectedArrangement = this.selectedArrangement();
    const selectedSetting = this.selectedSetting();
    const isShowingUnavailable = this.selectedAttributeService.shouldShowUnavailable();
    const reachableArrangements = this.reachableArrangementsWithSettings();
    const arrangements = this.arrangements()
    if (isShowingUnavailable) {
      const sortedArrangements = arrangements.sort((a, b) => (reachableArrangements.includes(b.id) ? 1 : 0) - (reachableArrangements.includes(a.id) ? 1 : 0));
      return sortedArrangements;
    } else {
      return arrangements.filter(w => reachableArrangements.includes(w.id) || (w.stoneArrangementID === selectedArrangement?.id && w.stoneSettingID === selectedSetting?.id));
    }
  });
  sizesToShow = computed(() => {
    const selectedSize = this.selectedSize();
    const isShowingUnavailable = this.selectedAttributeService.shouldShowUnavailable();
    const reachableSizes = this.reachableStoneSizes();
    const sizes = this.sizes()
    if (isShowingUnavailable) {
      const sortedSizes = sizes.sort((a, b) => (reachableSizes.includes(b.id) ? 1 : 0) - (reachableSizes.includes(a.id) ? 1 : 0));
      return sortedSizes;
    } else {
      return sizes.filter(w => reachableSizes.includes(w.id) || w.id === selectedSize?.id);
    }
  });

  constructor() {
    effect(async () => {
      this.loadingService.load('GemstonesComponent::GetReachable');
      const iframeID = untracked(() => this.checkoutService.isRetailerOnly()) ? this.checkoutService.retailerIframeID : null;
      const [reachableTypesWithShapes, reachableArrangementsWithSettings, reachableSizes] = await Promise.all([this.rulesService.getReachableStoneTypesWithShapes(this.selectedAttributeService.configuredItem(), iframeID), this.rulesService.getReachableStoneArrangementsWithSettings(this.selectedAttributeService.configuredItem(), iframeID), this.rulesService.getReachableStoneSizes(this.selectedAttributeService.configuredItem(), iframeID)]);
      this.reachableTypesWithShapes.set(reachableTypesWithShapes);
      this.reachableArrangementsWithSettings.set(reachableArrangementsWithSettings);
      this.reachableStoneSizes.set(reachableSizes);
      this.loadingService.done('GemstonesComponent::GetReachable');
    }, { allowSignalWrites: true });

    effect(async () => {
      this.loadingService.load('GemstonesComponent::Available');
      const availableAttributes = await this.selectedAttributeService.availableAttributes();
      this.availableTypesWithShapes.set(availableAttributes.stoneTypesWithShapes);
      this.availableArrangementsWithSettings.set(availableAttributes.stoneArrangementsWithSettings);
      this.availableStoneSizes.set(availableAttributes.stoneSizes);
      this.loadingService.done('GemstonesComponent::Available');
    }, {allowSignalWrites: true});

    effect(async () => {
      if (this.selectedAttributeService.configuredItem().gemstoneType) {
        this.loadingService.load('MainImage::DisplayedValue::StoneCounts');
        const stoneCount = await this.stoneCountsProvider.get(this.selectedAttributeService.configuredItem());
        this.stoneCount.set(stoneCount);
        this.loadingService.done('MainImage::DisplayedValue::StoneCounts');
      } else {
        this.stoneCount.set(0);
      }
    }, { allowSignalWrites: true });

    effect(() => {
      const arrangementWithSetting = this.stoneArrangementsWithSettingsService.values().find(aws => aws.stoneArrangementID === this.selectedAttributeService.configuredItem().gemstoneArrangement?.id && aws.stoneSettingID === this.selectedAttributeService.configuredItem().gemstoneSetting?.id);
      const typeWithShape = this.stoneTypesWithShapesService.values().find(tws => tws.stoneTypeID === this.selectedAttributeService.configuredItem().gemstoneType?.id && this.selectedAttributeService.configuredItem().gemstoneShape?.id === this.selectedAttributeService.configuredItem().gemstoneShape?.id);

      if (this.selectedAttributeService.configuredItem().gemstoneType) {
        this.navigationService.subtitle.set([`${this.stoneCount()} x ${this.selectedAttributeService.configuredItem().gemstoneSize?.displayName}`, `${typeWithShape?.displayName}`, `${arrangementWithSetting?.displayName}`].join(' '));
      } else {
        this.navigationService.subtitle.set(null);
      }

    }, { allowSignalWrites: true });

    effect(async () => {
      const reachableCounts = await this.reachableArrangementsWithSettings().length + this.reachableStoneSizes().length + this.reachableTypesWithShapes().length;
      const showLength = this.typesToShow().length + this.sizesToShow().length + this.arrangementsToShow().length
      const container = this.container();
      if (untracked(() => this.shouldScroll()) && container) {
        this.scrollService.scrollToPosition('stones', container.nativeElement);
        this.shouldScroll.set(reachableCounts !== showLength);
      }
    }, { allowSignalWrites: true });
  }

  ngOnDestroy() {
    const container = this.container();
    if (container) {
      this.scrollService.savePosition('stones', container.nativeElement.scrollTop);
    }
  }

  async typeSelected(typeWithShape: GemstoneTypeWithShape) {
    if (this.googleAnalyticsService.isInitialized) {
      this.googleAnalyticsService.triggerEvent('click', 'attribute_panel', 'gemstoneCutStone', typeWithShape.displayName);
    }

    if (this.selectedType()?.id !== typeWithShape.stoneTypeID || this.selectedShape()?.id !== typeWithShape.stoneShapeID) {
      if ((await this.availableTypesWithShapes()).includes(typeWithShape.id)) {
        await this.selectedAttributeService.mutate(c => {
          const matchingType = this.stoneTypesService.values().find(type => type.id === typeWithShape.stoneTypeID);
          assert(matchingType, 'No matching arrangement');

          const matchingShape = this.stoneShapesService.values().find(shape => shape.id === typeWithShape.stoneShapeID);
          assert(matchingShape, 'No matching setting');

          c.gemstoneType = matchingType;
          c.gemstoneShape = matchingShape;
        }, 'GemstoneTypeWithShape', undefined, true);
      } else {
        try {
          this.loadingService.load('SetClosestByStoneTypeWithShape');
          await this.selectedAttributeService.setClosestByStoneTypeWithShape(typeWithShape);
        } catch(e) {
          console.log(e);
          this.dialog.open(UnavailableDialogComponent, { maxWidth: 450, data: 'width' });
        } finally {
          this.loadingService.done('SetClosestByStoneTypeWithShape');
        }
      }
    }
  }

  async sizeSelected(size: GemstoneSize) {
    if (this.googleAnalyticsService.isInitialized) {
      this.googleAnalyticsService.triggerEvent('click', 'attribute_panel', 'gemstoneSize', size.displayName);
    }

    if (this.selectedSize()?.id !== size.id) {
      if ((await this.availableStoneSizes()).includes(size.id)) {
        await this.selectedAttributeService.mutate(c => {
          c.gemstoneSize = size;
        }, 'GemstoneSize', undefined, true);
      } else {
        try {
          this.loadingService.load('SetClosestByStoneSize');
          await this.selectedAttributeService.setClosestByStoneSize(size);
        } catch (e) {
          console.log(e);
          this.dialog.open(UnavailableDialogComponent, { maxWidth: 450, data: 'width' });
        } finally {
          this.loadingService.done('SetClosestByStoneSize');
        }
      }
    }
  }

  async arrangementSelected(arrangementWithSetting: GemstoneArrangementWithSetting) {
    if (this.googleAnalyticsService.isInitialized) {
      this.googleAnalyticsService.triggerEvent('click', 'attribute_panel', 'gemstoneArrangementWithSetting', arrangementWithSetting.displayName);
    }

    if (this.selectedArrangement()?.id !== arrangementWithSetting.stoneArrangementID || this.selectedSetting()?.id !== arrangementWithSetting.stoneSettingID) {
      if ((await this.availableArrangementsWithSettings()).includes(arrangementWithSetting.id)) {
        await this.selectedAttributeService.mutate(c => {
          const matchingArrangement = this.stoneArrangementsService.values().find(arrangement => arrangement.id === arrangementWithSetting.stoneArrangementID);
          assert(matchingArrangement, 'No matching arrangement');

          const matchingSetting = this.stoneSettingsService.values().find(setting => setting.id === arrangementWithSetting.stoneSettingID);
          assert(matchingSetting, 'No matching setting');

          c.gemstoneArrangement = matchingArrangement;
          c.gemstoneSetting = matchingSetting;
        }, 'GemstoneArrangementWithSetting', undefined, true);
      } else {
        try {
          this.loadingService.load('SetClosestByArrangementWithSetting');
          await this.selectedAttributeService.setClosestByStoneArrangementWithSetting(arrangementWithSetting);
        } catch (e) {
          console.log(e);
          this.dialog.open(UnavailableDialogComponent, { maxWidth: 450, data: 'width' });
        } finally {
          this.loadingService.done('SetClosestByArrangementWithSetting');
        }
      }
    }
  }

  async addStones() {
    const arrangementWithSetting = this.stoneArrangementsWithSettingsService.values()[0];
    const typeWithShape = this.stoneTypesWithShapesService.values()[0];

    const matchingArrangement = this.stoneArrangementsService.values().find(arrangement => arrangement.id === arrangementWithSetting.stoneArrangementID);
    assert(matchingArrangement, 'No matching arrangement');

    const matchingSetting = this.stoneSettingsService.values().find(setting => setting.id === arrangementWithSetting.stoneSettingID);
    assert(matchingSetting, 'No matching setting');

    const matchingType = this.stoneTypesService.values().find(type => type.id === typeWithShape.stoneTypeID);
    assert(matchingType, 'No matching arrangement');

    const matchingShape = this.stoneShapesService.values().find(shape => shape.id === typeWithShape.stoneShapeID);
    assert(matchingShape, 'No matching setting');

    const size = this.stoneSizesService.values()[0];

    const newConfiguredItemToValidate = Object.assign({}, this.selectedAttributeService.configuredItem(), { gemstoneType: matchingType, gemstoneSetting: matchingSetting, gemstoneSize: size, gemstoneArrangement: matchingArrangement, gemstoneShape: matchingShape });

    if (!await this.rulesService.getIsValid(newConfiguredItemToValidate, this.checkoutService.isRetailerOnly() ? this.checkoutService.retailerIframeID : null)) {
      this.loadingService.load('MainImage::SetClosestStoneType');
      await this.selectedAttributeService.setClosestByStoneTypeWithShape(typeWithShape, newConfiguredItemToValidate, true);
      this.loadingService.done('MainImage::SetClosestStoneType');
    } else {
      this.selectedAttributeService.mutate(c => {
        c.gemstoneType = matchingType;
        c.gemstoneSetting = matchingSetting;
        c.gemstoneSize = size;
        c.gemstoneArrangement = matchingArrangement;
        c.gemstoneShape = matchingShape;
      }, 'Gemstone');
    }
  }

  removeStones() {
    this.selectedAttributeService.removeGemstones();
  }
}

